/**
 * Smartwork ERP 챗봇 지식베이스
 * - 키워드 매칭 기반 FAQ/가이드 제공
 * - 외부 API 불필요, 자체 동작
 */

// ========== 카테고리별 지식베이스 ==========

export const knowledgeBase = [
  // ──────────────────────────────────────
  // 1. 로그인 / 계정 관련
  // ──────────────────────────────────────
  {
    id: 'login-fail',
    category: '계정',
    keywords: ['로그인', '접속', '안됨', '안돼', '비밀번호', '패스워드', '잊어', '잊었', '분실', '틀린', '오류', '실패'],
    question: '로그인이 안 돼요 / 비밀번호를 잊었어요',
    answer: `<b>로그인 문제 해결 방법:</b>
<ol>
<li><b>비밀번호 오류</b>: Caps Lock이 켜져 있는지 확인하세요.</li>
<li><b>비밀번호 분실</b>: 관리자에게 비밀번호 초기화를 요청하세요.</li>
<li><b>비밀번호 변경</b>: 로그인 후 우측 상단 사용자명 클릭 → "패스워드 변경" 메뉴에서 변경 가능합니다.</li>
<li><b>계정 잠김</b>: 5회 이상 실패 시 일시적으로 잠길 수 있습니다. 잠시 후 다시 시도하세요.</li>
</ol>
<p>💡 <b>팁</b>: 브라우저의 자동 완성 기능을 활용하면 편리합니다.</p>`,
    relatedIds: ['permission-denied', 'profile-edit']
  },
  {
    id: 'permission-denied',
    category: '계정',
    keywords: ['권한', '403', '접근', '거부', '볼 수 없', '메뉴가 없', '보이지 않', '안 보여', '안보여', '권한이 없', '페이지'],
    question: '특정 메뉴/페이지에 접근할 수 없어요 (403 오류)',
    answer: `<b>권한 부족 문제:</b>
<ol>
<li>Smartwork는 <b>역할 기반 권한</b> 시스템입니다. 각 메뉴는 특정 권한 그룹에 속해야 접근 가능합니다.</li>
<li><b>필요한 권한 확인</b>:
  <ul>
    <li>디자인: 디자인관리자</li>
    <li>샘플: 샘플관리자, 샘플승인자</li>
    <li>견적: 견적서관리자</li>
    <li>생산: 메인요청관리자, 생산관리자</li>
    <li>자재: 발주서관리자, 자재발주관리자</li>
    <li>무역: LC관리자</li>
    <li>원가: 원가조회자, 원가관리자</li>
    <li>지출결의서: 지출결의서작성자</li>
    <li>휴가관리: 인사담당자</li>
  </ul>
</li>
<li><b>해결 방법</b>: 시스템 관리자(Admin)에게 해당 권한 그룹 배정을 요청하세요.</li>
</ol>`,
    relatedIds: ['login-fail']
  },
  {
    id: 'profile-edit',
    category: '계정',
    keywords: ['프로필', '이름', '변경', '수정', '사진', '이미지', '개인정보', '내 정보'],
    question: '프로필 정보를 변경하고 싶어요',
    answer: `<b>프로필 수정 방법:</b>
<ol>
<li>우측 상단의 <b>사용자명</b>을 클릭합니다.</li>
<li><b>"프로필"</b> 메뉴를 선택합니다.</li>
<li>변경하려는 항목을 수정 후 <b>저장</b> 버튼을 클릭합니다.</li>
</ol>
<p>프로필 사진도 이 화면에서 업로드할 수 있습니다.</p>`,
    relatedIds: ['login-fail']
  },

  // ──────────────────────────────────────
  // 2. 디자인 관리
  // ──────────────────────────────────────
  {
    id: 'design-create',
    category: '디자인',
    keywords: ['디자인', '생성', '등록', '신규', '스타일', '만들기', '추가', '새로운'],
    question: '새 디자인(스타일)을 등록하는 방법',
    answer: `<b>디자인 등록 절차:</b>
<ol>
<li>상단 메뉴 <b>디자인 → 디자인 관리</b>로 이동합니다.</li>
<li>우측 상단 <b>"신규 등록"</b> 버튼을 클릭합니다.</li>
<li>필수 입력: <b>브랜드, 스타일번호, 시즌, 아이템</b></li>
<li>이미지를 첨부하면 목록에서 미리보기가 표시됩니다.</li>
<li><b>저장</b> 버튼을 클릭하면 등록 완료됩니다.</li>
</ol>
<p>💡 등록 후 상세 화면에서 추가 정보(원부자재, 작업지시 등)를 입력할 수 있습니다.</p>`,
    relatedIds: ['design-search', 'sample-request']
  },
  {
    id: 'design-search',
    category: '디자인',
    keywords: ['디자인', '검색', '찾기', '필터', '조회', '목록', '스마트뷰', '전체보기'],
    question: '디자인을 검색/필터링하는 방법',
    answer: `<b>디자인 검색 방법:</b>
<ol>
<li><b>스마트뷰</b>: 내가 담당하는 디자인만 표시됩니다. 빠른 업무 확인에 유용합니다.</li>
<li><b>전체보기</b>: 전체 디자인이 표시되며, 상단 필터로 세부 조건을 지정할 수 있습니다.</li>
<li><b>필터 조건</b>: 브랜드, 시즌, 상태 등으로 검색할 수 있습니다.</li>
<li>스타일번호나 디자인명으로 직접 검색도 가능합니다.</li>
</ol>
<p>💡 "스마트뷰 / 전체보기" 전환은 목록 상단의 버튼으로 가능합니다.</p>`,
    relatedIds: ['design-create']
  },

  // ──────────────────────────────────────
  // 3. 샘플 관리
  // ──────────────────────────────────────
  {
    id: 'sample-request',
    category: '샘플',
    keywords: ['샘플', '요청', '신청', '의뢰', '제작', '패턴', '봉재', '재단'],
    question: '샘플 요청을 생성하는 방법',
    answer: `<b>샘플 요청 생성:</b>
<ol>
<li>상단 메뉴 <b>디자인 → 샘플요청</b>으로 이동합니다.</li>
<li><b>"신규 요청"</b> 버튼을 클릭합니다.</li>
<li>필수 입력: <b>디자인(스타일), 브랜드, 납기일</b></li>
<li>저장하면 <b>"요청"</b> 상태로 생성됩니다.</li>
<li>이후 부서장 승인 → 최종 승인 단계를 거칩니다.</li>
</ol>
<p>상세 화면에서 패턴/재단/봉재 작업을 등록하고 진행 현황을 확인할 수 있습니다.</p>`,
    relatedIds: ['sample-approval', 'design-create']
  },
  {
    id: 'sample-approval',
    category: '샘플',
    keywords: ['샘플', '승인', '결재', '반려', '부서장'],
    question: '샘플 승인/반려 처리 방법',
    answer: `<b>샘플 승인 절차:</b>
<ol>
<li>샘플 요청이 들어오면 <b>승인자</b>에게 알림이 발송됩니다.</li>
<li><b>샘플요청 목록</b>에서 해당 건을 클릭하여 상세 화면으로 이동합니다.</li>
<li><b>"승인"</b> 또는 <b>"반려"</b> 버튼을 클릭합니다.</li>
<li>반려 시 사유를 입력해야 합니다.</li>
</ol>
<p>부서장 승인 → 최종 승인 순서로 진행됩니다.</p>`,
    relatedIds: ['sample-request']
  },

  // ──────────────────────────────────────
  // 4. 견적 관리
  // ──────────────────────────────────────
  {
    id: 'quotation-create',
    category: '견적',
    keywords: ['견적', '견적서', '작성', '생성', '등록', '원가', '단가', '가격'],
    question: '견적서를 작성하는 방법',
    answer: `<b>견적서 작성 절차:</b>
<ol>
<li>상단 메뉴 <b>디자인 → 견적</b>으로 이동합니다.</li>
<li><b>"신규 견적"</b> 버튼을 클릭합니다.</li>
<li>디자인(스타일)과 브랜드를 선택합니다.</li>
<li>원자재비, 부자재비, 임가공비(CMT), 물류비, 관세 등을 입력합니다.</li>
<li>환율을 설정하면 최종 견적가가 자동 계산됩니다.</li>
<li>완료 후 <b>"확정"</b>하면 메인요청(메인오더) 생성에 연계됩니다.</li>
</ol>`,
    relatedIds: ['quotation-print', 'main-request-create']
  },
  {
    id: 'quotation-print',
    category: '견적',
    keywords: ['견적', '인쇄', '출력', '프린트', 'PDF', '다운로드'],
    question: '견적서를 인쇄/출력하는 방법',
    answer: `<b>견적서 출력:</b>
<ol>
<li>견적 상세 화면에서 <b>"인쇄"</b> 버튼을 클릭합니다.</li>
<li>인쇄 미리보기 화면이 열립니다.</li>
<li>브라우저의 인쇄 기능(Ctrl+P)으로 출력하거나 PDF로 저장할 수 있습니다.</li>
</ol>`,
    relatedIds: ['quotation-create']
  },

  // ──────────────────────────────────────
  // 5. 생산 관리 (메인요청/메인오더)
  // ──────────────────────────────────────
  {
    id: 'main-request-create',
    category: '생산',
    keywords: ['작업지시', '메인요청', '생성', '등록', '신규', '발주', '생산'],
    question: '메인오더(메인요청)를 생성하는 방법',
    answer: `<b>메인오더 생성:</b>
<ol>
<li>상단 메뉴 <b>생산 → 메인오더</b>로 이동합니다.</li>
<li><b>"신규 요청"</b> 버튼을 클릭합니다.</li>
<li>필수 입력: <b>브랜드, 디자인, 발주수량, 납기일</b></li>
<li>색상별/사이즈별 수량을 배분합니다.</li>
<li>저장 후 <b>마일스톤(일정)</b>이 자동 생성됩니다.</li>
</ol>
<p>💡 메인오더는 전체 생산 프로세스의 중심이며, 자재발주, 원가, 무역 모든 정보가 이 단위로 집계됩니다.</p>`,
    relatedIds: ['main-request-status', 'milestone-manage', 'work-instruction']
  },
  {
    id: 'main-request-status',
    category: '생산',
    keywords: ['작업', '상태', '진행', '현황', '대시보드', '추적', '어디까지'],
    question: '작업 현황/진행 상태를 확인하는 방법',
    answer: `<b>작업 현황 확인:</b>
<ol>
<li><b>생산 → 작업현황</b>에서 전체 진행 현황을 대시보드로 확인할 수 있습니다.</li>
<li>각 메인오더의 상태: 요청접수 → 자재발주중 → 생산중 → 선적중 → 납품완료 → 정산완료</li>
<li><b>마일스톤 타임라인</b>: 메인오더 상세에서 각 단계별 예정일/완료일을 확인합니다.</li>
<li>지연된 마일스톤은 <b>빨간색</b>으로 표시됩니다.</li>
</ol>`,
    relatedIds: ['main-request-create', 'milestone-manage']
  },
  {
    id: 'milestone-manage',
    category: '생산',
    keywords: ['마일스톤', '일정', '스케줄', '납기', '지연', '알림', 'D-day'],
    question: '마일스톤(일정) 관리 방법',
    answer: `<b>마일스톤 관리:</b>
<ol>
<li>메인오더 생성 시 납기일 기준으로 <b>11단계 마일스톤</b>이 자동 생성됩니다.</li>
<li>각 마일스톤: 자재발주(D-60) → 원자재입고(D-45) → 부자재입고(D-40) → ... → 납품(D-0)</li>
<li><b>완료 처리</b>: 해당 마일스톤 항목의 완료 버튼을 클릭합니다.</li>
<li><b>지연 알림</b>: 예정일이 지나면 자동으로 관련 담당자에게 알림이 발송됩니다.</li>
<li>예정일 N일 전에 사전 알림도 발송됩니다.</li>
</ol>`,
    relatedIds: ['main-request-status']
  },
  {
    id: 'work-instruction',
    category: '생산',
    keywords: ['메인오더', '상세', '규격', '치수', '혼용율', '가공', '인쇄'],
    question: '메인오더 상세 내용 확인/출력',
    answer: `<b>메인오더 상세:</b>
<ol>
<li>메인오더 목록에서 해당 건을 클릭하면 상세 화면으로 이동합니다.</li>
<li><b>기본정보</b>: 브랜드, 디자인, 생산공장, 혼용율, 가공방법</li>
<li><b>규격(치수)</b>: 부위별/사이즈별 치수 정보</li>
<li><b>수량배분</b>: 색상별/사이즈별 생산수량</li>
<li><b>자재목록</b>: 필요 원부자재 리스트</li>
<li>인쇄 버튼으로 공장 전달용 문서를 출력할 수 있습니다.</li>
</ol>`,
    relatedIds: ['main-request-create']
  },
  {
    id: 'inspection-guide',
    category: '생산',
    keywords: ['검품', '검사', '품질', 'AQL', '불량', '합격', '불합격'],
    question: '검품 관리 방법',
    answer: `<b>검품 관리:</b>
<ol>
<li><b>생산 → 검품 관리</b>에서 검품 현황을 확인합니다.</li>
<li><b>신규 검품</b> 등록: 메인오더, 검품유형(중간/최종/AQL), 검사수량을 입력합니다.</li>
<li>검사 결과: <b>합격 / 불합격 / 조건부합격</b> 중 선택합니다.</li>
<li>불량수량을 기록하면 불량률이 자동 계산됩니다.</li>
<li>검품 보고서 파일을 첨부할 수 있습니다.</li>
</ol>`,
    relatedIds: ['main-request-status', 'delivery-guide']
  },
  {
    id: 'delivery-guide',
    category: '생산',
    keywords: ['납품', '반품', '배송', '출하', '납품완료', '부분납품'],
    question: '납품/반품 처리 방법',
    answer: `<b>납품 관리:</b>
<ol>
<li><b>생산 → 납품/반품</b> 화면으로 이동합니다.</li>
<li><b>납품 등록</b>: 메인오더, 납품예정일, 수량을 입력합니다.</li>
<li>실제 납품 후 <b>"납품완료"</b> 처리합니다.</li>
<li><b>부분납품</b>: 납품수량을 부분적으로 입력하면 자동으로 부분납품으로 처리됩니다.</li>
</ol>
<b>반품 처리:</b>
<ol>
<li>납품 상세에서 <b>"반품 등록"</b> 버튼을 클릭합니다.</li>
<li>반품일, 사유(불량/규격불일치/재고과다/기타), 수량을 입력합니다.</li>
</ol>`,
    relatedIds: ['inspection-guide']
  },

  // ──────────────────────────────────────
  // 6. 자재 관리
  // ──────────────────────────────────────
  {
    id: 'material-order-create',
    category: '자재',
    keywords: ['발주', '발주서', '자재', '주문', '구매', '원자재', '부자재', '오더'],
    question: '자재 발주서를 생성하는 방법',
    answer: `<b>발주서 생성:</b>
<ol>
<li>상단 메뉴 <b>자재 → 발주서</b>로 이동합니다.</li>
<li><b>"신규 발주"</b> 버튼을 클릭합니다.</li>
<li>매입처(공급업체)를 선택합니다.</li>
<li>발주할 자재 품목, 수량, 단가를 입력합니다.</li>
<li>메인오더와 연결하면 원가 자동 집계에 반영됩니다.</li>
<li>저장 후 인쇄하여 업체에 발송할 수 있습니다.</li>
</ol>`,
    relatedIds: ['material-receiving', 'material-dashboard']
  },
  {
    id: 'material-receiving',
    category: '자재',
    keywords: ['입고', '수령', '받았', '도착', '모바일', '바코드', 'QR', '패킹'],
    question: '자재 입고 처리 방법',
    answer: `<b>입고 처리 방법:</b>
<p><b>1. PC 입고:</b></p>
<ol>
<li><b>자재 → 입고 목록</b>에서 발주서를 선택합니다.</li>
<li>입고 수량을 입력하고 <b>"입고확인"</b>을 클릭합니다.</li>
</ol>
<p><b>2. 모바일 입고:</b></p>
<ol>
<li><b>자재 → 모바일 입고</b>에서 바코드/QR 스캔으로 빠르게 입고 처리합니다.</li>
<li>스마트폰에서 접속하면 모바일에 최적화된 화면이 표시됩니다.</li>
</ol>
<p><b>3. 모바일 패킹:</b></p>
<ol>
<li><b>자재 → 모바일 패킹</b>에서 패킹리스트를 생성합니다.</li>
</ol>`,
    relatedIds: ['material-order-create', 'material-transfer']
  },
  {
    id: 'material-dashboard',
    category: '자재',
    keywords: ['자재현황', '재고', '자재', '대시보드', '통계', '수불'],
    question: '자재 현황을 확인하는 방법',
    answer: `<b>자재현황 대시보드:</b>
<ol>
<li><b>자재 → 자재현황</b>으로 이동합니다.</li>
<li>전체 자재의 발주/입고/잔여 현황을 한눈에 확인할 수 있습니다.</li>
<li>메인오더별, 매입처별로 필터링 가능합니다.</li>
</ol>`,
    relatedIds: ['material-order-create']
  },
  {
    id: 'material-transfer',
    category: '자재',
    keywords: ['자재전달', '전달', '이동', '이송', '공장'],
    question: '자재 전달(이동) 처리 방법',
    answer: `<b>자재 전달:</b>
<ol>
<li><b>자재 → 자재전달</b>에서 전달 기록을 관리합니다.</li>
<li>출발지, 도착지(공장), 자재 품목, 수량을 입력합니다.</li>
<li>전달 확인 후 입고/출고 기록에 자동 반영됩니다.</li>
</ol>`,
    relatedIds: ['material-receiving']
  },
  {
    id: 'test-report-guide',
    category: '자재',
    keywords: ['시험', '성적서', '검사', '품질', '테스트', '리포트', '시험성적'],
    question: '시험성적서 관리 방법',
    answer: `<b>시험성적서:</b>
<ol>
<li><b>자재 → 시험성적서</b>에서 관리합니다.</li>
<li><b>신규 등록</b>: 메인오더, 자재명, 매입처, 시험항목, 결과를 입력합니다.</li>
<li>시험 성적서 파일(PDF 등)을 첨부할 수 있습니다.</li>
<li>유효기간 관리로 만료된 성적서를 파악할 수 있습니다.</li>
</ol>`,
    relatedIds: ['material-order-create']
  },

  // ──────────────────────────────────────
  // 7. 무역 관리
  // ──────────────────────────────────────
  {
    id: 'lc-manage',
    category: '무역',
    keywords: ['LC', 'L/C', '신용장', '개설', '어메', '어멘드', 'amendment', '네고', '매입'],
    question: 'LC(신용장) 관리 방법',
    answer: `<b>LC 관리:</b>
<ol>
<li><b>무역 → LC 관리</b>에서 LC를 등록하고 관리합니다.</li>
<li><b>LC 상태</b>: 개설신청 → 개설완료 → 선적 → 네고접수 → 매입완료</li>
<li>상세 화면에서 <b>"상태변경"</b> 드롭다운으로 다음 단계로 진행합니다.</li>
<li>Amendment(조건변경)가 필요하면 상세 화면에서 어멘드를 등록합니다.</li>
<li>관련 서류(L/C 원본 등)를 첨부할 수 있습니다.</li>
</ol>
<p>💡 LC관리자 권한이 필요합니다.</p>`,
    relatedIds: ['lg-manage', 'shipment-guide']
  },
  {
    id: 'lg-manage',
    category: '무역',
    keywords: ['LG', '보증서', '보증', '은행', 'guarantee'],
    question: 'LG(보증서) 관리 방법',
    answer: `<b>LG 관리:</b>
<ol>
<li><b>무역 → LG 관리</b>에서 보증서를 등록하고 관리합니다.</li>
<li>LC와 연결하여 관리할 수 있습니다.</li>
<li>보증은행, 금액, 통화, 발행일, 만료일 등을 입력합니다.</li>
</ol>`,
    relatedIds: ['lc-manage']
  },
  {
    id: 'shipment-guide',
    category: '무역',
    keywords: ['선적', '스케줄', '선박', '운송', '컨테이너', 'ETD', 'ETA', 'B/L'],
    question: '선적 스케줄 관리 방법',
    answer: `<b>선적 스케줄:</b>
<ol>
<li><b>무역 → 선적 스케줄</b>에서 관리합니다.</li>
<li>메인오더, 선적일, 도착예정일(ETA), B/L 번호를 입력합니다.</li>
<li>캘린더 뷰와 리스트 뷰를 전환하여 확인할 수 있습니다.</li>
<li>선적 지연 시 마일스톤에 자동 반영됩니다.</li>
</ol>`,
    relatedIds: ['lc-manage', 'freight-guide']
  },
  {
    id: 'freight-guide',
    category: '무역',
    keywords: ['운임', '해상운임', '항공운임', '물류비', '운송비', '프레이트'],
    question: '운임비용 관리 방법',
    answer: `<b>운임비용:</b>
<ol>
<li><b>무역 → 운임비용</b>에서 해상/항공/내륙 운임을 등록합니다.</li>
<li>메인오더와 연결하면 원가에 자동 집계됩니다.</li>
<li>운송사, 금액, 통화, 출발일/도착일을 입력합니다.</li>
</ol>`,
    relatedIds: ['shipment-guide']
  },
  {
    id: 'import-invoice-guide',
    category: '무역',
    keywords: ['수입송장', '인보이스', 'invoice', '통관', '수입'],
    question: '수입송장 관리 방법',
    answer: `<b>수입송장:</b>
<ol>
<li><b>무역 → 수입송장</b>에서 수입 물품의 송장을 관리합니다.</li>
<li>LC, 매입처, 금액, 환율, B/L 번호 등을 입력합니다.</li>
<li>도착예정일(ETA)을 입력하면 입고 일정 관리에 활용됩니다.</li>
</ol>`,
    relatedIds: ['lc-manage', 'freight-guide']
  },
  {
    id: 'tt-payment-guide',
    category: '무역',
    keywords: ['TT', '송금', 'T/T', '전신환', '해외송금', '외화'],
    question: 'TT 송금 관리 방법',
    answer: `<b>TT 송금:</b>
<ol>
<li><b>무역 → TT 송금</b>에서 해외 전신환 송금 내역을 관리합니다.</li>
<li>송금처, 금액, 통화, 송금일, 관련 메인오더를 입력합니다.</li>
<li>원가에 자동 집계됩니다.</li>
</ol>`,
    relatedIds: ['lc-manage']
  },
  {
    id: 'debit-note-guide',
    category: '무역',
    keywords: ['데빗노트', 'debit', 'note', '차감', '클레임'],
    question: '데빗노트 관리 방법',
    answer: `<b>데빗노트:</b>
<ol>
<li><b>무역 → 데빗노트</b>에서 품질 클레임 등에 의한 차감 내역을 관리합니다.</li>
<li>관련 메인오더, 사유, 금액을 입력합니다.</li>
</ol>`,
    relatedIds: ['lc-manage']
  },
  {
    id: 'daily-labor-guide',
    category: '무역',
    keywords: ['일용직', '노무비', '인건비', '작업자', '시급', '일당'],
    question: '일용직 노무비 관리 방법',
    answer: `<b>일용직 관리:</b>
<ol>
<li><b>무역 → 일용직</b>에서 일용직 작업/노무비를 관리합니다.</li>
<li>작업일, 작업자명, 작업내용, 시간, 시급을 입력합니다.</li>
<li>총금액이 자동 계산되며 원가에 반영됩니다.</li>
</ol>`,
    relatedIds: []
  },

  // ──────────────────────────────────────
  // 8. 원가 관리
  // ──────────────────────────────────────
  {
    id: 'cost-overview',
    category: '원가',
    keywords: ['원가', '비용', '총비용', '마진', '이익', '수익', '손익', '성과'],
    question: '원가 정보를 확인하는 방법',
    answer: `<b>원가 확인:</b>
<ol>
<li><b>원가조회자</b> 또는 <b>원가관리자</b> 권한이 필요합니다.</li>
<li><b>원가 → 원가 비교</b>에서 견적 원가 vs 실제 원가를 비교할 수 있습니다.</li>
<li>메인오더별로 원자재/부자재/임가공/경비 항목별 비교가 가능합니다.</li>
<li>차이율(%) 및 마진 분석 정보가 표시됩니다.</li>
</ol>
<p>💡 원가 정보가 보이지 않는 경우, 관리자에게 원가조회자 권한 배정을 요청하세요.</p>`,
    relatedIds: ['lot-manage', 'cost-category']
  },
  {
    id: 'lot-manage',
    category: '원가',
    keywords: ['LOT', '롯', '배분', '다운', '벌크', '자재', '잔여', '고가'],
    question: 'LOT 자재 배분 방법',
    answer: `<b>LOT 자재 관리:</b>
<ol>
<li><b>원가 → LOT 자재</b>에서 벌크 자재 LOT를 관리합니다.</li>
<li>LOT 등록: 자재명, 총수량, 단가, 매입처를 입력합니다.</li>
<li><b>배분</b>: 잔여수량이 있는 LOT의 "배분" 버튼을 클릭합니다.</li>
<li>특정 메인오더에 필요한 수량만큼 배분합니다.</li>
<li>배분된 수량은 해당 메인오더의 원가에 자동 반영됩니다.</li>
</ol>
<p>💡 다운 등 고가 벌크 자재는 LOT 단위로 구매 후 스타일별로 배분하는 방식입니다.</p>`,
    relatedIds: ['cost-overview']
  },
  {
    id: 'cost-category',
    category: '원가',
    keywords: ['원가', '카테고리', '분류', '항목', '경비', '비용항목'],
    question: '원가 카테고리 관리 방법',
    answer: `<b>원가 카테고리:</b>
<ol>
<li><b>원가 → 원가 카테고리</b>에서 비용 항목 분류를 관리합니다.</li>
<li>원자재, 부자재, 임가공, 경비 등 대분류를 관리합니다.</li>
<li>새로운 비용 항목을 추가하거나 기존 항목을 수정할 수 있습니다.</li>
</ol>`,
    relatedIds: ['cost-overview']
  },
  {
    id: 'executive-dashboard',
    category: '원가',
    keywords: ['경영', '대시보드', '총괄', '현황', '리포트', '보고서', '경영진'],
    question: '경영 대시보드 사용 방법',
    answer: `<b>경영 대시보드:</b>
<ol>
<li><b>원가 → 경영 대시보드</b>에서 전체 경영 현황을 확인합니다.</li>
<li>매출, 원가, 마진 트렌드를 그래프로 확인할 수 있습니다.</li>
<li>브랜드별, 시즌별 성과 분석이 가능합니다.</li>
</ol>
<p>💡 관리자(Superuser) 권한이 필요합니다.</p>`,
    relatedIds: ['cost-overview']
  },

  // ──────────────────────────────────────
  // 9. 지출결의서
  // ──────────────────────────────────────
  {
    id: 'expense-create',
    category: '지출결의서',
    keywords: ['지출', '결의서', '작성', '등록', '경비', '비용', '청구'],
    question: '지출결의서를 작성하는 방법',
    answer: `<b>지출결의서 작성:</b>
<ol>
<li>메뉴에서 <b>지출결의서 → 지출결의서</b>로 이동합니다.</li>
<li><b>"신규 작성"</b> 버튼을 클릭합니다.</li>
<li>지출 유형, 거래처, 금액 등을 입력합니다.</li>
<li>상세 항목에서 브랜드, 스타일번호, 메인오더를 연결하면 원가에 자동 집계됩니다.</li>
<li>저장 후 결재 요청을 할 수 있습니다.</li>
</ol>
<p>💡 지출결의서작성자 권한이 필요합니다.</p>`,
    relatedIds: ['expense-approval', 'expense-print']
  },
  {
    id: 'expense-approval',
    category: '지출결의서',
    keywords: ['지출', '승인', '결재', '접수', '집행', '반려', '결의서'],
    question: '지출결의서 결재/승인 처리 방법',
    answer: `<b>지출결의서 결재 흐름:</b>
<ol>
<li><b>작성 → 접수 → 집행 → 완료</b> 순서로 진행됩니다.</li>
<li><b>접수자</b>: 지출결의서접수자 권한 필요. 접수 또는 반려 처리합니다.</li>
<li><b>집행자</b>: 지출결의서집행자 권한 필요. 집행 처리하고 완료합니다.</li>
<li><b>일별지출통계</b>: 일자별 지출 현황을 집계하여 확인합니다.</li>
</ol>`,
    relatedIds: ['expense-create']
  },
  {
    id: 'expense-print',
    category: '지출결의서',
    keywords: ['지출', '인쇄', '출력', '프린트'],
    question: '지출결의서 출력 방법',
    answer: `<b>지출결의서 출력:</b>
<ol>
<li>지출결의서 상세 화면에서 <b>"인쇄"</b> 버튼을 클릭합니다.</li>
<li>인쇄 미리보기 화면이 열립니다.</li>
<li>Ctrl+P로 출력하거나 PDF로 저장합니다.</li>
</ol>`,
    relatedIds: ['expense-create']
  },

  // ──────────────────────────────────────
  // 10. 전자결재
  // ──────────────────────────────────────
  {
    id: 'approval-inbox',
    category: '결재',
    keywords: ['결재', '결재함', '대기', '미결', '승인', '반려', '전자결재'],
    question: '전자결재(결재함) 사용 방법',
    answer: `<b>결재함:</b>
<ol>
<li>메뉴에서 <b>결재함</b>을 클릭합니다.</li>
<li>나에게 도착한 결재 건 목록이 표시됩니다.</li>
<li>각 건을 클릭하여 상세 내용을 확인합니다.</li>
<li><b>"승인"</b> 또는 <b>"반려"</b> 버튼으로 처리합니다.</li>
<li>반려 시 사유를 반드시 입력해야 합니다.</li>
</ol>`,
    relatedIds: ['expense-approval']
  },

  // ──────────────────────────────────────
  // 11. 휴가 관리
  // ──────────────────────────────────────
  {
    id: 'leave-request',
    category: '휴가',
    keywords: ['휴가', '연차', '신청', '반차', '반일', '오전', '오후', '병가', '출산'],
    question: '휴가를 신청하는 방법',
    answer: `<b>휴가 신청:</b>
<ol>
<li>메뉴에서 <b>휴가 → 신청</b>으로 이동합니다.</li>
<li><b>"휴가 신청"</b> 버튼을 클릭합니다.</li>
<li>휴가 유형(연차/반차/병가/특별휴가 등)을 선택합니다.</li>
<li>시작일, 종료일, 사유를 입력합니다.</li>
<li>제출하면 승인자에게 결재 요청이 발송됩니다.</li>
</ol>
<p>💡 잔여 연차 일수는 신청 화면에서 확인할 수 있습니다.</p>`,
    relatedIds: ['leave-approval', 'leave-calendar']
  },
  {
    id: 'leave-approval',
    category: '휴가',
    keywords: ['휴가', '승인', '결재', '거절', '반려'],
    question: '휴가 승인/반려 처리 방법',
    answer: `<b>휴가 승인:</b>
<ol>
<li>메뉴에서 <b>휴가 → 승인</b>으로 이동합니다.</li>
<li>대기 중인 휴가 신청 목록이 표시됩니다.</li>
<li>각 건을 확인 후 <b>"승인"</b> 또는 <b>"반려"</b> 처리합니다.</li>
</ol>
<p>💡 휴가승인자 권한이 필요합니다.</p>`,
    relatedIds: ['leave-request']
  },
  {
    id: 'leave-calendar',
    category: '휴가',
    keywords: ['휴가', '달력', '캘린더', '일정', '팀원'],
    question: '휴가 달력 확인 방법',
    answer: `<b>휴가 달력:</b>
<ol>
<li>메뉴에서 <b>휴가 → 휴가달력</b>으로 이동합니다.</li>
<li>월별 달력에서 팀원들의 휴가 현황을 확인할 수 있습니다.</li>
<li>색상으로 휴가 유형을 구분합니다.</li>
</ol>`,
    relatedIds: ['leave-request']
  },

  // ──────────────────────────────────────
  // 12. 관리 (마스터 데이터)
  // ──────────────────────────────────────
  {
    id: 'customer-manage',
    category: '관리',
    keywords: ['고객', '브랜드', '거래처', '매출처', '바이어'],
    question: '고객사/브랜드 관리 방법',
    answer: `<b>고객사/브랜드:</b>
<ol>
<li>메뉴에서 <b>관리 → 고객사/브랜드</b>로 이동합니다.</li>
<li><b>"신규 등록"</b>으로 고객사를 추가합니다.</li>
<li>고객사 하위에 브랜드를 등록할 수 있습니다.</li>
<li>브랜드별로 디자인, 메인오더 등이 분류됩니다.</li>
</ol>`,
    relatedIds: ['supplier-manage', 'brand-assignment']
  },
  {
    id: 'supplier-manage',
    category: '관리',
    keywords: ['매입처', '공급업체', '거래처', '업체', '구매처', '매입'],
    question: '매입처(공급업체) 관리 방법',
    answer: `<b>매입처 관리:</b>
<ol>
<li>메뉴에서 <b>관리 → 매입처</b>로 이동합니다.</li>
<li>원부자재 공급업체를 등록하고 관리합니다.</li>
<li>계좌정보, 연락처를 등록할 수 있습니다.</li>
<li>발주서 작성 시 여기 등록된 매입처를 선택합니다.</li>
</ol>`,
    relatedIds: ['customer-manage', 'material-order-create']
  },
  {
    id: 'brand-assignment',
    category: '관리',
    keywords: ['브랜드', '담당자', '배정', '할당', '담당'],
    question: '브랜드 담당자 관리 방법',
    answer: `<b>브랜드 담당자:</b>
<ol>
<li>메뉴에서 <b>관리 → 브랜드 담당자</b>로 이동합니다.</li>
<li>각 브랜드에 대해 MD, 자재, 생산, 무역 등 역할별 담당자를 지정합니다.</li>
<li>담당자 지정에 따라 "스마트뷰"에서 자신의 브랜드만 필터링됩니다.</li>
</ol>`,
    relatedIds: ['customer-manage']
  },
  {
    id: 'notice-manage',
    category: '관리',
    keywords: ['공지', '공지사항', '게시판', '안내', '알림'],
    question: '공지사항 관리 방법',
    answer: `<b>공지사항:</b>
<ol>
<li>메뉴에서 <b>관리 → 공지사항</b>으로 이동합니다.</li>
<li>전체 공지사항 목록을 확인합니다.</li>
<li>공지사항관리자 권한이 있으면 새 공지를 작성할 수 있습니다.</li>
<li>팝업 기간을 설정하면 로그인 시 자동으로 팝업이 표시됩니다.</li>
</ol>`,
    relatedIds: []
  },

  // ──────────────────────────────────────
  // 13. 공통 기능 / 트러블슈팅
  // ──────────────────────────────────────
  {
    id: 'smart-view',
    category: '공통',
    keywords: ['스마트뷰', '내 업무', '내 담당', '필터', '마이', '나의'],
    question: '스마트뷰/전체보기 기능',
    answer: `<b>스마트뷰 vs 전체보기:</b>
<ol>
<li><b>스마트뷰</b>: 나의 브랜드/담당 업무만 표시합니다. 빠른 업무 처리에 적합합니다.</li>
<li><b>전체보기</b>: 모든 데이터가 표시됩니다. 전체 현황 파악에 적합합니다.</li>
<li>목록 화면 상단의 버튼으로 전환할 수 있습니다.</li>
<li>스마트뷰에서 데이터가 안 보이면 <b>브랜드 담당자</b>에 본인이 등록되어 있는지 확인하세요.</li>
</ol>`,
    relatedIds: ['brand-assignment']
  },
  {
    id: 'notification-guide',
    category: '공통',
    keywords: ['알림', '푸시', '통지', '노티', 'Push', '웹푸시'],
    question: '알림(Push) 설정 방법',
    answer: `<b>푸시 알림:</b>
<ol>
<li>처음 로그인 시 <b>"알림 권한 요청"</b> 버튼이 표시됩니다.</li>
<li>버튼을 클릭하여 브라우저 알림을 허용합니다.</li>
<li>이후 마일스톤 알림, 결재 요청 등이 자동으로 푸시됩니다.</li>
<li>알림이 안 오면 브라우저 설정에서 알림 차단 여부를 확인하세요.</li>
</ol>`,
    relatedIds: []
  },
  {
    id: 'print-guide',
    category: '공통',
    keywords: ['인쇄', '출력', '프린트', 'PDF', '저장'],
    question: '문서 인쇄/PDF 저장 방법',
    answer: `<b>문서 인쇄:</b>
<ol>
<li>견적서, 발주서, 지출결의서 등 인쇄 가능한 문서에는 <b>"인쇄"</b> 버튼이 있습니다.</li>
<li>버튼을 클릭하면 인쇄 전용 화면이 열립니다.</li>
<li><b>Ctrl + P</b>(Mac: Cmd + P)로 인쇄 대화상자를 엽니다.</li>
<li><b>PDF로 저장</b>: 프린터 선택에서 "PDF로 저장"을 선택하면 PDF 파일로 저장됩니다.</li>
</ol>`,
    relatedIds: []
  },
  {
    id: 'data-not-showing',
    category: '트러블슈팅',
    keywords: ['안 나와', '안나와', '비어', '빈 화면', '데이터 없', '목록이 없', '안 보여', '안보여', '로딩', '멈춤'],
    question: '데이터가 표시되지 않아요 / 화면이 비어 있어요',
    answer: `<b>데이터가 안 보이는 경우:</b>
<ol>
<li><b>스마트뷰</b> 모드인지 확인하세요. "전체보기"로 전환하면 모든 데이터가 표시됩니다.</li>
<li><b>필터</b>가 적용되어 있을 수 있습니다. 필터를 초기화해보세요.</li>
<li><b>권한</b>이 부족할 수 있습니다. 관리자에게 문의하세요.</li>
<li>브라우저를 <b>새로고침</b>(F5)해보세요.</li>
<li>위 방법으로 해결되지 않으면 <b>브라우저 캐시 삭제</b> 후 다시 시도하세요.</li>
</ol>`,
    relatedIds: ['smart-view', 'permission-denied', 'browser-error']
  },
  {
    id: 'browser-error',
    category: '트러블슈팅',
    keywords: ['에러', '오류', '버그', '하얀 화면', '블랭크', '깨짐', '멈춤', '무한', '로딩', '느림', '캐시'],
    question: '오류가 발생했어요 / 화면이 멈췄어요',
    answer: `<b>오류 해결 방법:</b>
<ol>
<li><b>새로고침</b>: F5 키를 눌러 페이지를 새로고침합니다.</li>
<li><b>강제 새로고침</b>: Ctrl+Shift+R (Mac: Cmd+Shift+R)로 캐시를 무시하고 새로고침합니다.</li>
<li><b>브라우저 캐시 삭제</b>:
  <ul>
    <li>Chrome: 설정 → 개인정보 → 인터넷 사용 기록 삭제</li>
    <li>"캐시된 이미지 및 파일"을 선택하고 삭제합니다.</li>
  </ul>
</li>
<li><b>다른 브라우저</b>로 시도해보세요. Chrome 최신 버전을 권장합니다.</li>
<li>문제가 지속되면 오류 화면을 캡처하여 관리자에게 전달하세요.</li>
</ol>`,
    relatedIds: ['data-not-showing']
  },
  {
    id: 'mobile-usage',
    category: '공통',
    keywords: ['모바일', '스마트폰', '핸드폰', '휴대폰', '태블릿', '아이패드', '안드로이드', '아이폰'],
    question: '모바일에서 사용하는 방법',
    answer: `<b>모바일 사용:</b>
<ol>
<li>스마트폰 브라우저에서 동일한 URL로 접속합니다.</li>
<li>화면 왼쪽 상단의 <b>햄버거 메뉴(≡)</b>를 터치하면 전체 메뉴가 열립니다.</li>
<li>모든 기능이 모바일에서도 사용 가능합니다.</li>
<li><b>모바일 입고</b>, <b>모바일 패킹</b>은 모바일에 최적화된 화면입니다.</li>
</ol>
<p>💡 북마크(즐겨찾기)로 추가하면 앱처럼 편리하게 접속할 수 있습니다.</p>`,
    relatedIds: []
  },
  {
    id: 'help-feature',
    category: '공통',
    keywords: ['도움말', '가이드', '매뉴얼', '사용법', '사용방법', '튜토리얼', '설명서'],
    question: '도움말/가이드 보는 방법',
    answer: `<b>도움말 기능:</b>
<ol>
<li>각 화면 우측 상단에 <b>"도움말"</b> 버튼이 있습니다.</li>
<li>클릭하면 해당 화면에 대한 상세 가이드가 표시됩니다.</li>
<li><b>3개 탭</b>: 화면 가이드 / 업무 흐름 / FAQ</li>
<li>화면별 맞춤 가이드를 제공합니다.</li>
</ol>
<p>💡 이 챗봇에서도 궁금한 것을 검색할 수 있습니다!</p>`,
    relatedIds: []
  },

  // ──────────────────────────────────────
  // 14. 업무 흐름
  // ──────────────────────────────────────
  {
    id: 'workflow-overview',
    category: '업무흐름',
    keywords: ['업무', '흐름', '프로세스', '절차', '단계', '순서', '워크플로우', '어떻게', '전체'],
    question: '전체 업무 흐름은 어떻게 되나요?',
    answer: `<b>Smartwork 전체 업무 흐름:</b>
<ol>
<li><b>샘플 단계</b>: 브랜드 요청 → 샘플요청 → 패턴/재단/봉제 → 샘플 완성</li>
<li><b>견적 단계</b>: 자재목록 기반 견적서 작성 → 고객 승인</li>
<li><b>메인요청(메인오더)</b>: 확정 견적 기반 → 색상/사이즈 배분 → 자재발주 → 마일스톤 생성</li>
<li><b>생산/무역</b>: 자재발주 → LC/LG 개설 → 자재선적 → 공장입고 → 생산 → 검품</li>
<li><b>납품/정산</b>: 선적 → 납품 → 반품처리 → 최종 원가 정산</li>
</ol>
<p><b>핵심</b>: 견적서(예상원가) vs 최종원가계산서(실제원가) 비교로 성과를 분석합니다.</p>`,
    relatedIds: ['main-request-create', 'cost-overview', 'sample-request', 'quotation-create']
  },

  // ──────────────────────────────────────
  // 15. 입출고 관리
  // ──────────────────────────────────────
  {
    id: 'inbound-outbound',
    category: '자재',
    keywords: ['입고관리', '출고관리', '입출고', '창고', '재고이동'],
    question: '입고/출고 관리 방법',
    answer: `<b>입출고 관리:</b>
<p><b>입고관리 (자재 → 입고관리):</b></p>
<ol>
<li>자재 또는 완제품의 입고를 기록합니다.</li>
<li>입고유형(자재입고/완제품입고), 메인오더, 매입처를 선택합니다.</li>
</ol>
<p><b>출고관리 (자재 → 출고관리):</b></p>
<ol>
<li>공장출고, 납품출고, 반품출고 유형을 선택합니다.</li>
<li>출고처와 수량을 입력합니다.</li>
</ol>`,
    relatedIds: ['material-receiving', 'delivery-guide']
  },

  // ──────────────────────────────────────
  // 16. 패킹리스트
  // ──────────────────────────────────────
  {
    id: 'packing-list',
    category: '자재',
    keywords: ['패킹', '리스트', '포장', '박스', '카톤'],
    question: '패킹리스트 관리 방법',
    answer: `<b>패킹리스트:</b>
<ol>
<li><b>자재 → 패킹리스트</b>에서 관리합니다.</li>
<li>메인오더별로 패킹 내역을 등록합니다.</li>
<li>박스 번호, 품목, 수량, 색상/사이즈를 입력합니다.</li>
<li>모바일 패킹 화면에서 현장에서 바로 입력할 수도 있습니다.</li>
</ol>`,
    relatedIds: ['material-receiving']
  }
]

// ========== 빠른 메뉴 네비게이션 ==========

export const quickMenus = [
  { label: '내 업무 대시보드', path: '/', icon: 'House' },
  { label: '디자인 관리', path: '/design', icon: 'Edit' },
  { label: '샘플요청', path: '/sample-requests', icon: 'Document' },
  { label: '견적', path: '/quotations', icon: 'Money' },
  { label: '작업현황', path: '/production-dashboard', icon: 'DataLine' },
  { label: '메인오더', path: '/main-requests', icon: 'Tickets' },
  { label: '검품 관리', path: '/inspections', icon: 'Checked' },
  { label: '납품/반품', path: '/deliveries', icon: 'Van' },
  { label: '자재현황', path: '/material-dashboard', icon: 'Goods' },
  { label: '발주서', path: '/material-orders', icon: 'ShoppingCart' },
  { label: '입고 목록', path: '/receivings', icon: 'Box' },
  { label: '시험성적서', path: '/test-reports', icon: 'DocumentChecked' },
  { label: 'LC 관리', path: '/lcs', icon: 'Coin' },
  { label: '선적 스케줄', path: '/shipments', icon: 'Ship' },
  { label: 'LOT 자재', path: '/material-lots', icon: 'Grid' },
  { label: '원가 비교', path: '/cost-comparison', icon: 'TrendCharts' },
  { label: '결재함', path: '/approval/inbox', icon: 'Stamp' },
  { label: '지출결의서', path: '/expenses', icon: 'Wallet' },
  { label: '휴가 신청', path: '/leave/request', icon: 'Calendar' },
  { label: '공지사항', path: '/notices', icon: 'Bell' },
  { label: '프로필', path: '/profile', icon: 'User' }
]

// ========== 검색 엔진 ==========

/**
 * 지식베이스에서 키워드 매칭으로 답변 검색
 * @param {string} query - 사용자 질문
 * @returns {Array} - 매칭된 답변 배열 (점수 순)
 */
export function searchKnowledge(query) {
  if (!query || query.trim().length < 1) return []

  const normalizedQuery = query.toLowerCase().trim()
  const queryTokens = normalizedQuery
    .replace(/[?？!！.,。，]/g, '')
    .split(/\s+/)
    .filter(t => t.length > 0)

  const results = []

  for (const item of knowledgeBase) {
    let score = 0

    // 1. 키워드 매칭 (핵심 로직)
    for (const keyword of item.keywords) {
      const kw = keyword.toLowerCase()
      if (normalizedQuery.includes(kw)) {
        score += kw.length * 3 // 긴 키워드일수록 가중치
      }
      // 부분 매칭
      for (const token of queryTokens) {
        if (kw.includes(token) || token.includes(kw)) {
          score += 2
        }
      }
    }

    // 2. 질문 텍스트 매칭
    const questionLower = item.question.toLowerCase()
    for (const token of queryTokens) {
      if (questionLower.includes(token)) {
        score += 1
      }
    }

    // 3. 카테고리 매칭
    if (normalizedQuery.includes(item.category.toLowerCase())) {
      score += 3
    }

    if (score > 0) {
      results.push({ ...item, score })
    }
  }

  // 점수 높은 순 정렬
  results.sort((a, b) => b.score - a.score)

  return results.slice(0, 5) // 최대 5개 반환
}

/**
 * 빠른 메뉴 검색
 */
export function searchMenus(query) {
  if (!query || query.trim().length < 1) return []
  const q = query.toLowerCase().trim()
  return quickMenus.filter(m => m.label.toLowerCase().includes(q))
}

/**
 * ID로 지식 항목 찾기
 */
export function getKnowledgeById(id) {
  return knowledgeBase.find(item => item.id === id)
}

/**
 * 카테고리별 항목 가져오기
 */
export function getByCategory(category) {
  return knowledgeBase.filter(item => item.category === category)
}

/**
 * 모든 카테고리 목록
 */
export function getCategories() {
  const cats = [...new Set(knowledgeBase.map(item => item.category))]
  return cats
}

// ========== 인사말 / 기본 응답 ==========

export const greetings = [
  '안녕하세요! Smartwork 도우미입니다. 무엇을 도와드릴까요?',
  '반갑습니다! 시스템 사용 중 궁금한 점이 있으면 질문해주세요.',
  'Smartwork 도우미입니다. 어떤 도움이 필요하신가요?'
]

export const fallbackResponse = `<p>죄송합니다. 질문에 대한 정확한 답변을 찾지 못했습니다.</p>
<p>아래 방법을 시도해보세요:</p>
<ul>
<li>다른 키워드로 다시 질문해보세요 (예: "메인오더 생성", "LC 상태변경")</li>
<li>아래 카테고리에서 원하는 항목을 선택해보세요</li>
<li>각 화면의 <b>"도움말"</b> 버튼을 활용해보세요</li>
</ul>
<p>지속적으로 문제가 있다면 시스템 관리자에게 문의하세요.</p>`
